import React, { useMemo } from "react";
import { HiCheckCircle, HiOutlineTrash } from "react-icons/hi";
import InputField from "components/common/Input/Input";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import { capitalizeAllLetters, parseBooleanVal } from "Utils/utils";
import { buildTaskGroupsForParticipants } from "./participantShiftTasksUtils";

/**
 * Shift tasks and per-participant notes — shared by basic and advanced shift edit.
 * Group shifts with multiple participants render tasks separated by participant.
 */
const ShiftTasksNotesSection = ({
  tasks = [],
  notes = [],
  selectedShift,
  checkRunningShiftStatus,
  onTaskChange,
  onTaskMandatoryChange,
  onAddTask,
  onTaskDelete,
  onNoteChange,
  className = "",
  embedded = false,
  hideHeader = false,
  children = null,
  isGroupShift = false,
  selectedParticipant = null,
}) => {
  const readOnly = checkRunningShiftStatus?.(selectedShift?.status);

  const participantList = useMemo(() => {
    if (Array.isArray(selectedParticipant)) {
      return selectedParticipant.filter(Boolean);
    }
    return selectedParticipant ? [selectedParticipant] : [];
  }, [selectedParticipant]);

  const groupByParticipant =
    parseBooleanVal(isGroupShift) && participantList.length > 1;

  // Grouping is UI-only for basic multi-participant group shifts.
  // Single participant, non-group, and callers that omit these props keep the flat list.

  const taskGroups = useMemo(() => {
    if (!groupByParticipant) return null;
    return buildTaskGroupsForParticipants(
      participantList,
      tasks,
      (participant) =>
        `${capitalizeAllLetters(participant?.first_name || "")} ${capitalizeAllLetters(
          participant?.last_name || ""
        )}`.trim()
    );
  }, [groupByParticipant, participantList, tasks]);

  const renderTaskRow = (taskObj, index) => {
    if (!taskObj) return null;
    return (
      <div key={index} className='flex items-center gap-2 mb-1.5'>
        <div className='flex-1 min-w-0'>
          <InputField
            readOnly={readOnly}
            placeholder='Add your tasks here...'
            value={taskObj.task}
            handleChange={(e) => onTaskChange?.(index, e)}
            type='text'
            name={`task[${index}]`}
            icon={
              taskObj?.is_completed && (
                <HiCheckCircle
                  className='h-5 w-5 text-green-500'
                  title='Task Completed'
                />
              )
            }
          />
        </div>

        <div className='flex-shrink-0 pt-1'>
          <SwitchWithIcon
            isSmall
            checked={parseBooleanVal(taskObj?.is_mandatory)}
            onChange={(value) => onTaskMandatoryChange?.(index, value)}
            disabled={readOnly}
            tooltip='Mandatory task'
          />
        </div>

        <div className='pt-3 flex-shrink-0'>
          <div
            onClick={() => !readOnly && onTaskDelete?.(index)}
            tabIndex={0}
            role='button'
            className='icon-delete-wrapper'
            title='Delete Task'
          >
            <HiOutlineTrash className='icon-size' />
          </div>
        </div>
      </div>
    );
  };

  const renderAddMoreTasksButton = (ptId = null) => (
    <div className='mt-1'>
      <button
        type='button'
        onClick={() => onAddTask?.(ptId)}
        disabled={readOnly}
        className='text-[#4B6CB7] text-xs font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
      >
        + Add More Tasks
      </button>
    </div>
  );

  const renderTasks = () => {
    if (groupByParticipant && taskGroups) {
      return (
        <>
          {taskGroups.groups.map((group) => (
            <div key={`task-group-${group.ptId || group.name}`} className='mb-3'>
              <label className='text-xs font-semibold text-gray-700 mb-1 block'>
                Tasks for {group.name}
              </label>
              {group.indexes.map((index) => renderTaskRow(tasks[index], index))}
              {renderAddMoreTasksButton(group.ptId)}
            </div>
          ))}
        </>
      );
    }

    return (
      <>
        {tasks.map((taskObj, index) => renderTaskRow(taskObj, index))}
        {/* Keep prior single/non-group behavior: no explicit ptId arg. */}
        {renderAddMoreTasksButton()}
      </>
    );
  };

  const content = (
    <>
      {!hideHeader && (
        <div className='flex items-center gap-1.5 mb-2'>
          <div className='w-6 h-6 rounded bg-orange-100 flex items-center justify-center flex-shrink-0'>
            <svg
              className='w-4 h-4 text-orange-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              aria-hidden
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
              />
            </svg>
          </div>
          <h3 className='text-xs font-bold text-gray-900'>Tasks & Notes</h3>
        </div>
      )}

      {renderTasks()}

      {notes?.map((note, index) => (
        <div key={index} className='mb-2 mt-2'>
          <label className='text-xs font-semibold text-gray-700 mb-1 block'>
            Notes for {capitalizeAllLetters(note?.first_name)}{" "}
            {capitalizeAllLetters(note?.last_name)}
          </label>
          <textarea
            className='bg-gray-50 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all w-full text-xs'
            rows={1}
            value={note.notes || ""}
            readOnly={readOnly}
            onChange={(e) => onNoteChange?.(index, e.target.value)}
          />
        </div>
      ))}
      {children}
    </>
  );

  if (embedded) {
    return <div className={className}>{content}</div>;
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-2.5 ${className}`}
    >
      {content}
    </div>
  );
};

export default ShiftTasksNotesSection;
